import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import {
  type BookingConfirmation,
  BookingForm,
  publicExcursionQueryOptions,
} from '@/features/public-booking'
import { fmtInterval } from '@/shared/lib'
import { Card } from '@/shared/ui/card'
import { Icon } from '@/shared/ui/icon'
import { Spinner } from '@/shared/ui/spinner'
import { StatusScreen } from '@/shared/ui/status-screen'

interface BookingSearch {
  t: string
}

/**
 * Публичная запись. Живёт вне гейта кабинета: контур анонимный, единственная
 * защита — секрет ссылки. Ответ на неверный секрет и на несуществующий выход
 * одинаков, иначе выходы можно перебрать.
 *
 * Страницу открывают во встроенном браузере Telegram/ВК — никаких внешних
 * ассетов и тяжёлых зависимостей на этом маршруте.
 */
export const Route = createFileRoute('/b/$excursionId')({
  validateSearch: (search: Record<string, unknown>): BookingSearch => ({
    t: typeof search.t === 'string' ? search.t : '',
  }),
  component: PublicBookingPage,
})

function PublicBookingPage() {
  const { excursionId } = Route.useParams()
  const { t } = Route.useSearch()
  const [done, setDone] = useState<BookingConfirmation | null>(null)

  const excursionQuery = useQuery({
    ...publicExcursionQueryOptions(excursionId, t),
    enabled: t.length > 0,
  })

  if (t.length === 0 || excursionQuery.isError) {
    return <StatusScreen title="Ссылка не действует" message="Попросите новую у команды." />
  }

  if (excursionQuery.isLoading) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  const excursion = excursionQuery.data
  if (!excursion) return null

  if (done) {
    return (
      <StatusScreen title="Записали" message={`${done.title} · ${fmtInterval(done.slot)}`}>
        <p className="text-sm text-text-secondary">
          {done.meetingPoint
            ? `Встречаемся: ${done.meetingPoint}`
            : 'Место встречи уточнит команда'}
        </p>
      </StatusScreen>
    )
  }

  return (
    <div className="mx-auto flex min-h-full w-full max-w-md flex-col gap-6 px-4 pt-safe-8 pb-safe-8">
      <header className="flex flex-col gap-2">
        <span className="label">запись на выход</span>
        <h1 className="t-h2">{excursion.title}</h1>
        <p className="mono-num text-sm text-text-secondary">{fmtInterval(excursion.slot)}</p>
        {excursion.meetingPoint && (
          <p className="flex items-center gap-2 text-sm text-text-secondary">
            <Icon name="pin" size={16} />
            {excursion.meetingPoint}
          </p>
        )}
      </header>

      {excursion.cancelled ? (
        <Card className="text-sm text-warning">
          Выход отменён{excursion.cancellationReason ? `: ${excursion.cancellationReason}` : ''}.
        </Card>
      ) : !excursion.seatsAvailable ? (
        <Card className="text-sm text-text-secondary">
          Мест не осталось. Напишите команде — вдруг кто-то откажется.
        </Card>
      ) : (
        <BookingForm excursionId={excursionId} excursion={excursion} token={t} onDone={setDone} />
      )}
    </div>
  )
}
